/**
 * watches the feature collection and keeps it in sync with a vector layer
 * @namespace commands
 * @module feature-collection-handler
 * @see {@link http://wiki.infor.com:8080/confluence/label/~CALIX/feature-collection-handler|blog}
 */

import ol = require("ol");
import Utils = require("../utils");
import Portal = require("portal-gis");
import Mediator = require("../mediator");
import MapView = require("../views/MapView");
import Templates = require("../models/defaults");
import SymbologyUtils = require("../utils/symbology");
import Base = require("./base-command");
import Converter = require("../utils/ol3/converter");

function asGeom(geom: string) {
    if (!geom) return null;
    // we don't require brackets for point features
    return JSON.parse(0 === geom.indexOf("[") ? geom : `[${geom}]`)
}

class Command extends Base<any> {

    static about = `
Creates a vector layer and renders application-level features.

Usage Example:

\`\`\`xml
<COMMAND about="watches the feature collection and keeps it in sync with a vector layer" disabled="False" id="feature-collection-handler" mid="app/commands/feature-collection-handler" type="startup" />
\`\`\`

`;

    static options = {
        "symbology": {
            "about": "if symbology set on the handler use it as the default feature symbology",
        },
    }

    execute(args = this.args) {

        let mediator = this.mediator;
        let command = this.configuration;
        let converter = new Converter();

        mediator.mapView(mapView => {

            if (mediator.api.layers.some(l => l.id === command.id)) {
                throw `${command.id} may only execute once because it renders maplet-level features`;
            }

            let layer = Templates.asLayerTemplate({
                id: command.id,
                type: "app/layer-factory/configuration-features", // rename to vector?
                text: command.text
            });

            // if symbology set on the handler use it as the default feature symbology
            let symbology = this.options["symbology"];

            // layer has no features yet, it is used to renderg all the maplet features
            mapView.addLayer(layer).done((layer: ol.layer.Vector) => {

                // watch the markers collection
                var features = this.mediator.api.features;
                var vectorSource = layer.getSource();

                /** watch for `geom` changes and move the marker */
                features.on("change:geom", (featureModel: typeof features.ModelClass) => {

                    if (!featureModel.data.geom) return;

                    let olFeature = <ol.Feature>this.mediator.extend(featureModel.data).olFeature;
                    if (!olFeature) return;

                    // why assume this is a point?
                    {
                        let geom = asGeom(featureModel.data.geom);
                        let olGeom: ol.geom.Geometry;

                        switch (featureModel.data.type) {
                            case "point":
                                olGeom = new ol.geom.Point(geom);
                                break;
                            case "line":
                                olGeom = new ol.geom.LineString(geom);
                                break;
                            case "polygon":
                                olGeom = new ol.geom.Polygon(geom);
                                break;
                            case "multipoint":
                                olGeom = new ol.geom.MultiPoint(geom);
                                break;
                            case "multiline":
                                olGeom = new ol.geom.MultiLineString(geom);
                                break;
                            case "multipolygon":
                                olGeom = new ol.geom.MultiPolygon(geom);
                                break;
                            default:
                                olGeom = new ol.geom.Point(geom);
                                break;
                        }
                        if (!olGeom) return;

                        // model is always 4326 but map may be in any projection so transform the coordinate
                        // before updating the feature location.
                        olGeom.transform("EPSG:4326", mapView.getProjection());
                        olFeature.setGeometry(olGeom);
                    }

                });

                let renderFeature = (featureModel: typeof features.ModelClass) => {

                    // feature has no geometry so we cannot add it to the map
                    if (!featureModel.data.geom) return;

                    let options = Utils.asDictionary(featureModel.data.Options.Values);

                    let feature = <Rhythm.GIS.FeatureQueryResultFeature>{
                        attributes: Utils.asDictionary(featureModel.data.Attributes.Values),
                        geometry: asGeom(featureModel.data.geom),
                        geometryType: featureModel.data.type
                    };

                    // if feature has no explicit symbology use the layer symbology
                    options["symbology"] = options["symbology"] || symbology;
                    let olFeature = SymbologyUtils.copyToMap([feature], mapView, vectorSource, options)[0];

                    // reference back to the source feature so we can add feature-level commands in the info-viewer
                    this.mediator.extend(olFeature, {
                        config: featureModel.data
                    });

                    // reference forward to the ol feature so we can update an existing feature
                    this.mediator.extend(featureModel.data, {
                        olFeature: olFeature
                    });

                    featureModel.once("remove", () => {
                        // (olFeature in vectorSource.featureChangeKeys_) && 
                        featureModel.collection.remove(featureModel);
                        try {
                            vectorSource.removeFeature(olFeature);
                        } catch (ex) {
                            console.error(ex);
                        }
                    });

                };

                // render current features
                features.forEach(renderFeature);

                // render features when they are added
                features.on("add", renderFeature);
            });
        });

    }

}

export = Command; 